<script setup>
import { ref, computed, watch } from 'vue';
import { ShieldAlert, Megaphone } from '@lucide/vue';
import Badge from '@/components/ui/Badge.vue';
import Button from '@/components/ui/Button.vue';
import { useAnnouncementsStore } from '@/stores/announcements';
import { useAuthStore } from '@/stores/auth';
import { formatDate } from '@/lib/format';

// Okno nieprzeczytanych ogłoszeń po zalogowaniu. Ważne wymagają aktywnego
// potwierdzenia („Zapoznałem się") — bez tego nie da się zamknąć; nieważne
// zamyka się przyciskiem (oznaczane jako przeczytane, nie wracają).
const store = useAnnouncementsStore();
const auth  = useAuthStore();

const queue  = ref([]);
const acting = ref(false);
let loadedFor = null;

const current = computed(() => queue.value[0] ?? null);

watch(() => auth.isAuthenticated, async (ok) => {
    if (ok && auth.user?.id && auth.user.id !== loadedFor) {
        loadedFor = auth.user.id;
        try {
            queue.value = await store.fetchPending();
        } catch {
            queue.value = [];
        }
    }
    if (!ok) {
        loadedFor = null;
        queue.value = [];
    }
}, { immediate: true });

function next() {
    queue.value = queue.value.slice(1);
}

async function acknowledge() {
    if (acting.value) return;
    acting.value = true;
    try {
        await store.acknowledge(current.value.id);
        next();
    } finally {
        acting.value = false;
    }
}

async function dismiss() {
    if (acting.value) return;
    acting.value = true;
    try {
        await store.markRead(current.value.id);
        next();
    } finally {
        acting.value = false;
    }
}
</script>

<template>
    <Teleport to="body">
        <div
            v-if="current"
            class="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
        >
            <div class="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-lg bg-popover shadow-xl ring-1 ring-foreground/10">
                <!-- Nagłówek -->
                <div class="flex items-center gap-2 border-b px-5 py-3">
                    <component :is="current.is_important ? ShieldAlert : Megaphone" class="h-5 w-5 shrink-0" :class="current.is_important ? 'text-warning' : 'text-muted-foreground'" />
                    <h2 class="min-w-0 truncate text-base font-semibold">{{ current.title }}</h2>
                    <Badge v-if="current.is_important" variant="outline" class="ml-1 shrink-0 border-warning/50 text-warning">Ważne</Badge>
                    <span v-if="queue.length > 1" class="ml-auto shrink-0 text-xs text-muted-foreground">
                        1 z {{ queue.length }}
                    </span>
                </div>

                <!-- Treść -->
                <div class="overflow-y-auto px-5 py-4">
                    <p class="whitespace-pre-wrap text-sm">{{ current.body }}</p>
                    <p v-if="current.published_at" class="mt-3 text-xs text-muted-foreground">
                        {{ current.created_by }} · {{ formatDate(current.published_at) }}
                    </p>
                </div>

                <!-- Akcje -->
                <div class="flex items-center justify-end gap-2 border-t px-5 py-3">
                    <template v-if="current.is_important">
                        <span class="mr-auto text-xs text-muted-foreground">Wymaga potwierdzenia zapoznania.</span>
                        <Button data-testid="announcement-popup-action" :disabled="acting" @click="acknowledge">Zapoznałem się z treścią</Button>
                    </template>
                    <template v-else>
                        <Button variant="outline" data-testid="announcement-popup-action" :disabled="acting" @click="dismiss">OK, zamknij</Button>
                    </template>
                </div>
            </div>
        </div>
    </Teleport>
</template>
